import React, { useState, useEffect, useCallback } from 'react';
import { config } from '../../data/config';
import MultiAutocompleteSelect from '../../../../components/Multiautoselecttags/Multiautocompleteselecttags';
import { findKgsPaginated } from '../../../cabinet/wizplain/activity';
import { InputMask } from '@react-input/mask';
// ─── Константи ────────────────────────────────────────────────────────────────

const KG_ROLE_LABELS = {
  kindergartenHead: 'Директор',
  educator: 'Вихователь',
  methodist: 'Вихователь-методист',
  nurse: 'Медична сестра',
};

const SUPPORT_ROLE_LABELS = {
  techassistance: 'Оператор служби техпідтримки',
};

const getRoleLabel = (role) =>
  KG_ROLE_LABELS[role] ||
  SUPPORT_ROLE_LABELS[role] ||
  config.rolesName[config.roles.indexOf(role)] ||
  role;

const KG_ROLES = Object.keys(KG_ROLE_LABELS);

// Правила валідації
const VALIDATORS = {
  lastName:   v => v?.trim() ? null : "Обов'язкове поле",
  firstName:  v => v?.trim() ? null : "Обов'язкове поле",
  middleName: v => v?.trim() ? null : "Обов'язкове поле",
  email:      v => {
    if (!v?.trim()) return "Обов'язкове поле";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Невірний формат email';
    return null;
  },
  sex:        v => v ? null : "Оберіть стать",
  area:       (v, formData) => {
    if (!v?.trim()) return "Обов'язкове поле";
    if (KG_ROLES.includes(formData?.role) && !/^[a-f0-9]{24}$/i.test(v)) {
      return 'Оберіть садок зі списку';
    }
    return null;
  },
  phone:      v => {
    if (!v) return null;
    const digits = v.replace(/\D/g, '');
    // маска +38(___) ___-____ = 12 цифр коли заповнена
    if (digits.length > 0 && digits.length < 12) return 'Введіть повний номер телефону';
    return null;
  },
  code:       v => {
    if (!v) return "Обов'язкове поле";
    if (!/^\d{10}$/.test(v)) return 'РНОКПП — 10 цифр';
    const digits = v.split('').map(Number);
    const weights = [-1, 5, 7, 9, 4, 6, 10, 5, 7];
    let control = digits.slice(0, 9).reduce((sum, d, i) => sum + d * weights[i], 0) % 11;
    if (control === 10) control = 0;
    if (control !== digits[9]) return 'Невірний РНОКПП (помилка контрольної суми)';
    return null;
  },
};

const REQUIRED_FIELDS = ['lastName', 'firstName', 'middleName', 'email', 'role', 'area', 'code', 'sex'];

const EMPTY_FORM = {
  id: '', firstName: '', lastName: '', middleName: '',
  sex: '', birthDate: '', citizenchip: 'Україна', birthPlace: '',
  passport__documentNumber__serial: '', passport__documentNumber__number: 0,
  passport__issueDate: '', passport__authority: '',
  code: '', email: '', phone: '',
  role: 'kindergartenHead', area: 'Київ', grant: '',
  isActive: false, privelegesAuthType: ''
};

// ─── Допоміжні UI ─────────────────────────────────────────────────────────────

const FieldError = ({ message }) =>
  message ? <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{message}</div> : null;

const StatusBadge = ({ isActive }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
    background: isActive ? '#d1fae5' : '#f3f4f6',
    color: isActive ? '#065f46' : '#6b7280',
    border: `1px solid ${isActive ? '#a7f3d0' : '#e5e7eb'}`
  }}>
    <span style={{
      width: '7px', height: '7px', borderRadius: '50%',
      background: isActive ? '#10b981' : '#9ca3af',
      display: 'inline-block'
    }} />
    {isActive ? 'Активний' : 'Деактивований'}
  </span>
);

// Відображення поля у режимі перегляду
const ViewField = ({ label, value, mono }) => (
  <div style={{ marginBottom: '14px' }}>
    <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>
      {label}
    </div>
    <div style={{ fontSize: '14px', color: value ? '#111827' : '#d1d5db', fontFamily: mono ? 'monospace' : 'inherit' }}>
      {value || '—'}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{
    fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
    letterSpacing: '0.08em', marginBottom: '12px', paddingBottom: '6px',
    borderBottom: '1px solid #e5e7eb'
  }}>
    {children}
  </div>
);

// ─── Головний компонент ───────────────────────────────────────────────────────

const AddEditUser = ({ action, data, onSave, onClose, errorFields = [] }) => {
  const isNew = action === 'add';

  const [mode, setMode] = useState(isNew ? 'edit' : 'view');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [localState, setLocalState] = useState({ area: 'Київ', role: 'kindergartenHead', privelegesAuthType: '' });
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [passportOpen, setPassportOpen] = useState(false);

  // Пошук садків через API
  const [kgOptions, setKgOptions] = useState([]);
  const [kgById, setKgById] = useState({});
  const [kgLoading, setKgLoading] = useState(false);
  const [kgHasMore, setKgHasMore] = useState(false);
  const [kgSearchTerm, setKgSearchTerm] = useState('');
  const [kgPage, setKgPage] = useState(0);

  const loadKindergartens = useCallback(async (search, pageNum = 0, append = false) => {
    if (pageNum === 0) setKgLoading(true);
    try {
      const result = await findKgsPaginated({ search, page: pageNum });
      const newOptions = result.items.map(item => ({
        _id: item._id,
        name: item.name,
        description: item.district || ''
      }));
      setKgOptions(prev => append ? [...prev, ...newOptions] : newOptions);
      setKgById(prev => {
        const next = { ...prev };
        newOptions.forEach(opt => { next[opt._id] = { name: opt.name }; });
        return next;
      });
      setKgHasMore(result.hasMore);
      setKgPage(pageNum);
    } catch (e) {
      console.error('Error loading kindergartens:', e);
    } finally {
      setKgLoading(false);
    }
  }, []);

  const handleKgSearch = useCallback((searchValue) => {
    setKgSearchTerm(searchValue);
    loadKindergartens(searchValue, 0, false);
  }, [loadKindergartens]);

  const handleKgLoadMore = useCallback(() => {
    if (!kgLoading && kgHasMore) {
      loadKindergartens(kgSearchTerm, kgPage + 1, true);
    }
  }, [kgLoading, kgHasMore, kgSearchTerm, kgPage, loadKindergartens]);

  const roles_kg = [config.roles[0], config.roles[3], config.roles[4]];

  useEffect(() => {
    if (!isNew && data) {
      const fd = {
        ...data,
        birthDate: data.birthDate ? data.birthDate.split('T')[0] : '',
        passport__issueDate: data.passport__issueDate ? data.passport__issueDate.split('T')[0] : ''
      };
      setFormData(fd);
      setLocalState({ area: data.area, role: data.role, privelegesAuthType: data.privelegesAuthType || '' });
      setPassportOpen(false);

      // Якщо роль садкова і є _id — підтягуємо назву для view-режиму
      if (KG_ROLES.includes(data.role) && data.area?.match(/^[a-f0-9]{24}$/i)) {
        loadKindergartens(data.name || '', 0, false);
      }
    }
  }, [action, data]);

  // Синхронізуємо зовнішні errorFields з локальним станом (при submit)
  useEffect(() => {
    if (errorFields.length > 0) {
      const newTouched = {};
      errorFields.forEach(f => { newTouched[f] = true; });
      setTouched(prev => ({ ...prev, ...newTouched }));
    }
  }, [errorFields]);

  // Валідація конкретного поля
  const validateField = useCallback((field, value, currentFormData) => {
    const validator = VALIDATORS[field];
    return validator ? validator(value, currentFormData || formData) : null;
  }, [formData]);

  // Валідація всієї форми
  const validateAll = useCallback((data) => {
    const newErrors = {};
    REQUIRED_FIELDS.forEach(field => {
      const validator = VALIDATORS[field];
      const err = validator ? validator(data[field], data) : null;
      if (err) newErrors[field] = err;
    });
    // додаткові поля з валідаторами (не обов'язкові але з форматом)
    ['phone'].forEach(field => {
      const validator = VALIDATORS[field];
      const err = validator ? validator(data[field], data) : null;
      if (err) newErrors[field] = err;
    });
    return newErrors;
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const err = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: err }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const err = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  const handleRoleChange = (e) => {
    const value = e.target.value;
    const checkIdentity = (a, b) =>
      (roles_kg.includes(a) && roles_kg.includes(b)) ||
      (!roles_kg.includes(a) && !roles_kg.includes(b));

    const newArea = checkIdentity(value, localState.role) ? localState.area :
      !isNew && checkIdentity(value, data.role) ? data.area :
      !roles_kg.includes(value) ? 'Київ' : '';

    setFormData(prev => ({ ...prev, role: value, area: newArea }));
    setLocalState(prev => ({ ...prev, role: value, area: newArea }));
    if (touched.role) {
      setErrors(prev => ({ ...prev, role: validateField('role', value) }));
    }
  };

  const handleSubmit = () => {
    const allTouched = {};
    REQUIRED_FIELDS.forEach(f => { allTouched[f] = true; });
    setTouched(prev => ({ ...prev, ...allTouched }));

    const newErrors = validateAll(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;
    onSave(formData);
  };

  const getFieldStyle = (field) => {
    const hasError = touched[field] && errors[field];
    const isOk = touched[field] && !errors[field] && REQUIRED_FIELDS.includes(field) && formData[field];
    return {
      width: '100%', padding: '8px 10px', fontSize: '14px',
      border: `1px solid ${hasError ? '#dc3545' : isOk ? '#10b981' : '#d1d5db'}`,
      borderRadius: '6px', outline: 'none',
      background: hasError ? '#fff5f5' : '#fff',
      transition: 'border-color 0.15s ease'
    };
  };

  const getSelectStyle = (field) => ({ ...getFieldStyle(field), cursor: 'pointer' });

  // Скільки помилок залишилось
  const errorCount = Object.values(errors).filter(Boolean).length;

  // ─── Режим перегляду ─────────────────────────────────────────────────────────

  if (mode === 'view' && !isNew) {
    const fullName = [formData.lastName, formData.firstName, formData.middleName].filter(Boolean).join(' ');
    const initials = [formData.lastName, formData.firstName]
      .filter(Boolean).map(n => n[0]).join('').toUpperCase();

    return (
      <div style={{ fontFamily: 'system-ui, sans-serif' }}>
        {/* Хедер карточки */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          padding: '20px 24px', background: 'linear-gradient(135deg, #f0f4ff 0%, #fafafa 100%)',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '18px', fontWeight: 700, flexShrink: 0
          }}>
            {initials || '?'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '17px', fontWeight: 700, color: '#111827' }}>{fullName || '—'}</div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{getRoleLabel(formData.role)}</div>
          </div>
          <StatusBadge isActive={formData.isActive} />
        </div>

        {/* Тіло */}
        <div style={{ padding: '20px 24px', maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>

            {/* Ліва колонка */}
            <div>
              <SectionTitle>Особисті дані</SectionTitle>
              <ViewField label="Стать" value={formData.sex === 'ж' ? 'Жіноча' : formData.sex === 'ч' ? 'Чоловіча' : formData.sex} />
              <ViewField label="Дата народження" value={formData.birthDate} />
              <ViewField label="Місце народження" value={formData.birthPlace} />
              <ViewField label="РНОКПП" value={formData.code} mono />

              <SectionTitle>Паспорт</SectionTitle>
              <ViewField label="Серія" value={formData.passport__documentNumber__serial} mono />
              <ViewField label="Номер" value={formData.passport__documentNumber__number || ''} mono />
              <ViewField label="Дата видачі" value={formData.passport__issueDate} />
              <ViewField label="Орган, що видав" value={formData.passport__authority} />
            </div>

            {/* Права колонка */}
            <div>
              <SectionTitle>Контакти та доступ</SectionTitle>
              <ViewField label="Email" value={formData.email} mono />
              <ViewField label="Телефон" value={formData.phone} mono />
              <ViewField label="Тип авторизації" value={config.authType[formData.privelegesAuthType]} />

              <SectionTitle>Роль і призначення</SectionTitle>
              <ViewField label="Роль" value={getRoleLabel(formData.role)} />
              <ViewField
                label={KG_ROLES.includes(formData.role) ? 'Садок' : 'Район'}
                value={KG_ROLES.includes(formData.role)
                  ? (kgById[formData.area]?.name || formData.area)
                  : formData.area
                }
              />
              <ViewField label="Права (CRUD)" value={formData.grant} mono />
            </div>
          </div>
        </div>

        {/* Футер */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '10px',
          padding: '16px 24px', borderTop: '1px solid #e5e7eb', background: '#fafafa'
        }}>
          <button type="button" onClick={onClose} style={btnStyle('secondary')}>
            Закрити
          </button>
          <button type="button" onClick={() => setMode('edit')} style={btnStyle('primary')}>
            ✏️ Редагувати
          </button>
        </div>
      </div>
    );
  }

  // ─── Режим редагування ────────────────────────────────────────────────────────

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Хедер форми */}
      <div style={{
        padding: '16px 24px', background: '#f8fafc',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>
            {isNew ? 'Новий користувач' : 'Редагування'}
          </div>
          {!isNew && (
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
              {[formData.lastName, formData.firstName].filter(Boolean).join(' ')}
            </div>
          )}
        </div>
        {errorCount > 0 && (
          <div style={{
            padding: '4px 10px', background: '#fee2e2', color: '#dc3545',
            borderRadius: '20px', fontSize: '12px', fontWeight: 600
          }}>
            {errorCount} {errorCount === 1 ? 'помилка' : 'помилки'}
          </div>
        )}
      </div>

      {/* Форма — три рівні зі скролом */}
      <div style={{ maxHeight: 'calc(90vh - 160px)', overflowY: 'auto' }}>

      {/* ── Рівень 1: ПІБ + особисті + паспорт (згортається) ── */}
      <div style={{ padding: '20px 24px 0', borderBottom: '1px solid #e5e7eb' }}>
        <SectionTitle>Особисті дані</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
          {/* Ліва: ПІБ */}
          <div>
            <FormRow label="Прізвище *">
              <input style={getFieldStyle('lastName')} value={formData.lastName}
                onChange={e => handleInputChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')} />
              <FieldError message={touched.lastName && errors.lastName} />
            </FormRow>
            <FormRow label="Ім'я *">
              <input style={getFieldStyle('firstName')} value={formData.firstName}
                onChange={e => handleInputChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')} />
              <FieldError message={touched.firstName && errors.firstName} />
            </FormRow>
            <FormRow label="По батькові *">
              <input style={getFieldStyle('middleName')} value={formData.middleName}
                onChange={e => handleInputChange('middleName', e.target.value)}
                onBlur={() => handleBlur('middleName')} />
              <FieldError message={touched.middleName && errors.middleName} />
            </FormRow>
          </div>
          {/* Права: стать, дата, громадянство, РНОКПП */}
          <div>
            <FormRow label="Стать *">
              <select style={getSelectStyle('sex')} value={formData.sex}
                onChange={e => handleInputChange('sex', e.target.value)}
                onBlur={() => handleBlur('sex')}>
                <option value="">— Оберіть —</option>
                <option value="ж">Жіноча</option>
                <option value="ч">Чоловіча</option>
              </select>
              <FieldError message={touched.sex && errors.sex} />
            </FormRow>
            <FormRow label="Дата народження">
              <input type="date" style={getFieldStyle('birthDate')} value={formData.birthDate}
                onChange={e => handleInputChange('birthDate', e.target.value)} />
            </FormRow>
            <FormRow label="РНОКПП *">
              <input style={getFieldStyle('code')} value={formData.code}
                onChange={e => handleInputChange('code', e.target.value)}
                onBlur={() => handleBlur('code')}
                placeholder="10 цифр" maxLength={10} />
              <FieldError message={touched.code && errors.code} />
            </FormRow>
          </div>
        </div>

        {/* Перемикач паспорта */}
        <div
          onClick={() => setPassportOpen(p => !p)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            cursor: 'pointer', color: '#3b82f6', fontSize: '13px', fontWeight: 600,
            marginBottom: '14px', userSelect: 'none'
          }}
        >
          <span style={{
            display: 'inline-block', width: '16px', height: '16px',
            lineHeight: '16px', textAlign: 'center', fontSize: '10px',
            transform: passportOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>▶</span>
          Паспортні дані
        </div>

        {/* Паспорт — розкривається */}
        {passportOpen && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px', marginBottom: '16px' }}>
            <div>
              <FormRow label="Серія">
                <input style={getFieldStyle('passport__documentNumber__serial')}
                  value={formData.passport__documentNumber__serial}
                  onChange={e => handleInputChange('passport__documentNumber__serial', e.target.value)} />
              </FormRow>
              <FormRow label="Номер">
                <input type="number" style={getFieldStyle('passport__documentNumber__number')}
                  value={formData.passport__documentNumber__number}
                  onChange={e => handleInputChange('passport__documentNumber__number', parseInt(e.target.value) || 0)} />
              </FormRow>
            </div>
            <div>
              <FormRow label="Дата видачі">
                <input type="date" style={getFieldStyle('passport__issueDate')}
                  value={formData.passport__issueDate}
                  onChange={e => handleInputChange('passport__issueDate', e.target.value)} />
              </FormRow>
              <FormRow label="Місце народження">
                <input style={getFieldStyle('birthPlace')} value={formData.birthPlace}
                  onChange={e => handleInputChange('birthPlace', e.target.value)} />
              </FormRow>
              <FormRow label="Орган, що видав">
                <input style={getFieldStyle('passport__authority')} value={formData.passport__authority}
                  onChange={e => handleInputChange('passport__authority', e.target.value)} />
              </FormRow>
            </div>
          </div>
        )}
      </div>

      {/* ── Рівень 2: Контакти ── */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
        <SectionTitle>Контакти</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
          <FormRow label="Email *">
            <input style={getFieldStyle('email')} value={formData.email}
              onChange={e => handleInputChange('email', e.target.value.trim())}
              onBlur={() => handleBlur('email')}
              placeholder="user@example.com" />
            <FieldError message={touched.email && errors.email} />
          </FormRow>
          <FormRow label="Телефон">
            <InputMask
              mask="+38(___) ___-____"
              replacement={{ _: /\d/ }}
              value={formData.phone}
              onChange={e => handleInputChange('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              style={getFieldStyle('phone')}
              placeholder="+38(0XX) XXX-XXXX"
            />
            <FieldError message={touched.phone && errors.phone} />
          </FormRow>
        </div>
      </div>

      {/* ── Рівень 3: Роль, тип авторизації, область дії ── */}
      <div style={{ padding: '16px 24px' }}>
        <SectionTitle>Роль і доступ</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
          <div>
            <FormRow label="Роль *">
              <select style={getSelectStyle('role')} value={localState.role}
                onChange={handleRoleChange} onBlur={() => handleBlur('role')}>
                {config.roles.map((item, i) => (
                  <option key={item} value={item}>{config.rolesName[i]}</option>
                ))}
              </select>
              <FieldError message={touched.role && errors.role} />
            </FormRow>
            <FormRow label="Тип авторизації">
              <select style={getSelectStyle('privelegesAuthType')} value={localState.privelegesAuthType}
                onChange={e => { handleInputChange('privelegesAuthType', e.target.value); setLocalState(p => ({ ...p, privelegesAuthType: e.target.value })); }}>
                {Object.keys(config.authType).map(el => (
                  <option key={el} value={el}>{config.authType[el]}</option>
                ))}
              </select>
            </FormRow>
          </div>
          <div>
            {KG_ROLES.includes(localState.role) ? (
              <FormRow label="Садок *">
                <MultiAutocompleteSelect
                  value={formData.area ? [formData.area] : []}
                  onChange={selected => {
                    const prev = formData.area;
                    const val = selected.find(id => id !== prev) || selected[selected.length - 1] || '';
                    handleInputChange('area', val);
                    setLocalState(p => ({ ...p, area: val }));
                    if (touched.area) {
                      setErrors(prev => ({ ...prev, area: validateField('area', val) }));
                    }
                  }}
                  options={kgOptions}
                  placeholder="Пошук садочка..."
                  fieldsValue={['name']}
                  loading={kgLoading}
                  hasMore={kgHasMore}
                  onSearch={handleKgSearch}
                  onLoadMore={handleKgLoadMore}
                  extraOptionsById={kgById}
                  error={touched.area && errors.area ? errors.area : ''}
                />
                <FieldError message={touched.area && errors.area} />
              </FormRow>
            ) : (
              <FormRow label="Район *">
                <select style={getSelectStyle('area')} value={localState.area}
                  onChange={e => { handleInputChange('area', e.target.value); setLocalState(p => ({ ...p, area: e.target.value })); }}
                  onBlur={() => handleBlur('area')}>
                  <option value="Київ">Київ</option>
                  {config.districts.map(item => <option key={item} value={item}>{item}</option>)}
                </select>
                <FieldError message={touched.area && errors.area} />
              </FormRow>
            )}
            <FormRow label="Активний">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
                <div
                  onClick={() => handleInputChange('isActive', !formData.isActive)}
                  style={{
                    width: '40px', height: '22px', borderRadius: '11px', position: 'relative',
                    background: formData.isActive ? '#10b981' : '#d1d5db',
                    transition: 'background 0.2s ease', cursor: 'pointer', flexShrink: 0
                  }}
                >
                  <div style={{
                    position: 'absolute', top: '3px',
                    left: formData.isActive ? '21px' : '3px',
                    width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                    transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </div>
                <span style={{ fontSize: '14px', color: formData.isActive ? '#065f46' : '#6b7280' }}>
                  {formData.isActive ? 'Активний' : 'Деактивований'}
                </span>
              </label>
            </FormRow>
          </div>
        </div>
      </div>
      </div>{/* кінець скролу */}

      {/* Футер */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: '10px',
        padding: '16px 24px', borderTop: '1px solid #e5e7eb', background: '#fafafa'
      }}>
        {!isNew && (
          <button type="button" onClick={() => { setMode('view'); setTouched({}); setErrors({}); }} style={btnStyle('ghost')}>
            Скасувати
          </button>
        )}
        {isNew && (
          <button type="button" onClick={onClose} style={btnStyle('ghost')}>
            Скасувати
          </button>
        )}
        <button type="button" onClick={handleSubmit} style={btnStyle('primary')}>
          {isNew ? 'Додати користувача' : 'Зберегти зміни'}
        </button>
      </div>
    </div>
  );
};

// ─── Маленькі хелпери ─────────────────────────────────────────────────────────

const FormRow = ({ label, children }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
      {label}
    </label>
    {children}
  </div>
);

const btnStyle = (variant) => {
  const base = {
    padding: '9px 20px', borderRadius: '6px', fontSize: '14px',
    fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'all 0.15s ease'
  };
  if (variant === 'primary') return { ...base, background: '#3b82f6', color: '#fff' };
  if (variant === 'secondary') return { ...base, background: '#e5e7eb', color: '#374151' };
  if (variant === 'ghost') return { ...base, background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb' };
};

export default AddEditUser;